/**
 * Authentication Service with Microsoft 365 SSO Integration
 *
 * Roles for QB Archive: Admin, Accounting
 */

import { PublicClientApplication } from '@azure/msal-browser';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.isAuthenticated = false;
    this.msalInstance = null;
    this.isInitialized = false;
  }

  async initialize() {
    if (this.isInitialized) return;

    const msalConfig = {
      auth: {
        clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '',
        authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID || 'common'}`,
        redirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: 'localStorage', // localStorage enables cross-tab SSO; MSAL handles token security internally
        storeAuthStateInCookie: false,
      },
    };

    try {
      this.msalInstance = new PublicClientApplication(msalConfig);
      await this.msalInstance.initialize();
      this.isInitialized = true;

      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalInstance.setActiveAccount(accounts[0]);
        await this.loadCurrentUser();
      }
    } catch (error) {
      console.error('Failed to initialize MSAL:', error);
      throw error;
    }
  }

  async signIn() {
    if (!this.isInitialized) {
      await this.initialize();
    }

    const loginRequest = {
      scopes: ['User.Read']
    };

    try {
      const loginResponse = await this.msalInstance.loginPopup(loginRequest);
      this.msalInstance.setActiveAccount(loginResponse.account);
      await this.loadCurrentUser();
      return this.currentUser;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  async loadCurrentUser() {
    const account = this.msalInstance.getActiveAccount();
    if (!account) return;

    try {
      const accessToken = await this.getAccessToken();
      const userProfile = await this.getUserProfile(accessToken);

      const userData = {
        id: account.localAccountId,
        entraId: account.localAccountId,
        email: account.username,
        firstName: userProfile.givenName || '',
        lastName: userProfile.surname || '',
        displayName: userProfile.displayName || account.name,
        department: userProfile.department || '',
        jobTitle: userProfile.jobTitle || '',
      };

      try {
        const syncedUser = await this.syncUserToDatabase(userData);
        userData.id = syncedUser.id;
        userData.entraId = account.localAccountId;
        userData.roles = syncedUser.roles || ['User'];
        userData.role = syncedUser.role || 'User';
      } catch (err) {
        if (err.status === 403) {
          throw err;  // Access denied — do not authenticate
        }
        console.warn('Failed to sync user to database:', err);
        userData.roles = ['User'];
        userData.role = 'User';
      }

      this.currentUser = userData;
      this.isAuthenticated = true;
      localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
    } catch (error) {
      if (error.status === 403) {
        this.isAuthenticated = false;
        throw error;  // Propagate to signIn → Login UI
      }
      console.error('Failed to load user:', error);
      this.currentUser = {
        id: account.localAccountId,
        entraId: account.localAccountId,
        email: account.username,
        displayName: account.name,
        firstName: '',
        lastName: '',
        department: '',
        jobTitle: '',
        roles: ['User'],
        role: 'User',
      };
      this.isAuthenticated = true;
    }
  }

  async syncUserToDatabase(user) {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        console.warn('REACT_APP_API_URL not configured, skipping database sync');
        return { roles: ['User'], role: 'User' };
      }
      const response = await fetch(`${apiUrl}/users/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entraId: user.id,
          email: user.email,
          name: user.displayName,
          firstName: user.firstName,
          lastName: user.lastName,
          department: user.department,
          jobTitle: user.jobTitle,
        })
      });

      if (response.status === 403) {
        const data = await response.json().catch(() => ({}));
        const err = new Error(data.message || 'You are not authorized to access this application.');
        err.status = 403;
        throw err;
      }

      if (!response.ok) {
        throw new Error(`Sync failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to sync user to database:', error);
      throw error;
    }
  }

  async getAccessToken() {
    const account = this.msalInstance.getActiveAccount();
    const tokenRequest = {
      scopes: ['User.Read'],
      account: account
    };

    try {
      const response = await this.msalInstance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (error) {
      console.warn('Silent token acquisition failed, using popup');
      const response = await this.msalInstance.acquireTokenPopup(tokenRequest);
      return response.accessToken;
    }
  }

  async getIdToken(forceRefresh = false) {
    const account = this.msalInstance.getActiveAccount();
    if (!account) {
      throw new Error('No active account');
    }

    const tokenRequest = {
      scopes: ['openid', 'profile', 'email'],
      account: account,
      forceRefresh: forceRefresh,
    };

    try {
      const response = await this.msalInstance.acquireTokenSilent(tokenRequest);
      return response.idToken;
    } catch (error) {
      console.warn('Silent ID token acquisition failed, using popup');
      const response = await this.msalInstance.acquireTokenPopup(tokenRequest);
      return response.idToken;
    }
  }

  async getUserProfile(accessToken) {
    const response = await fetch('https://graph.microsoft.com/v1.0/me', {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user profile');
    }

    return await response.json();
  }

  async getCurrentUserPhoto() {
    try {
      const accessToken = await this.getAccessToken();
      const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });

      if (!response.ok) return null;

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      return null;
    }
  }

  async signOut() {
    try {
      this.msalInstance.setActiveAccount(null);
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('msal.')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Logout cleanup error:', error);
    } finally {
      this.currentUser = null;
      this.isAuthenticated = false;
      localStorage.removeItem('currentUser');
    }
  }

  getCurrentUser() {
    if (this.currentUser) return this.currentUser;

    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      this.currentUser = JSON.parse(savedUser);
      this.isAuthenticated = true;
      return this.currentUser;
    }

    return null;
  }

  isUserAuthenticated() {
    return this.isAuthenticated;
  }

  hasRole(role) {
    const userRoles = this.currentUser?.roles || [this.currentUser?.role];
    return userRoles.includes(role);
  }

  isAdmin() {
    return this.hasRole('Admin');
  }
}

const authService = new AuthService();
export default authService;
